import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AbcCore } from '@barthauer/abc/core';
import {
  AuthenticationInterceptor,
  LoginInfo,
  ProfileInfo,
  ProfileService,
  UserPersonInfoService,
  UserService,
} from '@barthauer/abc/core/authentication';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { BehaviorSubject, merge, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, mapTo, publishReplay, refCount, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'aptis-authentication-profile-selection',
  templateUrl: './profile-selection.component.html',
  styleUrls: ['./profile-selection.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
})
export class ProfileSelectionComponent implements OnInit, OnDestroy {
  private readonly refreshToken$ = new BehaviorSubject(undefined);
  private readonly destroy$ = new Subject<void>(); // used to cancel Observable chains when component is destroyed
  private ForwardToRoutePath: string;
  private ForceReloadOnApply: boolean;
  private ApplicationTitle: string;
  public showLoader: boolean = false;
  public selectedProfilesName: string;
  public res = ResourceSet.Empty;
  loginInfo$: Observable<LoginInfo>;
  isLoadingProfiles$: Observable<boolean>;
  profiles$: Observable<ProfileInfo[]>;
  selectedProfiles: ProfileInfo[];
  isApplyingProfile: boolean;
  ErrorMessage: string;
  // location: any;
  public userInfo: any;
  selectedIndex: number;

  constructor(
    private router: Router,
    private userService: UserService,
    private profileService: ProfileService,
    private abcCore: AbcCore,
    private route: ActivatedRoute,
    private location: Location,
    private iconRegistry: MatIconRegistry,
    private resourceService: ResourceManagerService,
    sanitizer: DomSanitizer,
    public userPersonInfoService: UserPersonInfoService
  ) {
    iconRegistry.addSvgIcon(
      'profile-apply',
      sanitizer.bypassSecurityTrustResourceUrl('assets/images/profile-selection/apply.svg')
    );
    iconRegistry.addSvgIcon(
      'profile-refresh',
      sanitizer.bypassSecurityTrustResourceUrl('assets/images/profile-selection/refresh.svg')
    );
    iconRegistry.addSvgIcon('auth_error', sanitizer.bypassSecurityTrustResourceUrl('assets/images/auth_warning.svg'));

    iconRegistry.addSvgIcon('profile-logout', sanitizer.bypassSecurityTrustResourceUrl('global/images/loggout.svg'));

    this.route.data.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.ApplicationTitle = data.ApplicationTitle;
        this.ForwardToRoutePath = data.ForwardTo;
        this.ForceReloadOnApply = data.ForceReloadOnApply;
      },
      error: (err) => this.abcCore.error.publish(err),
    });

    this.ErrorMessage = null;
  }

  async ngOnInit() {
    this.res = await this.resourceService.getResourceSet('abc-core-authentication');
    this.ErrorMessage = null;
    this.userPersonInfoService.getCurrentUserPersonInfo().subscribe((data) => {
      this.userInfo = data;
    });

    this.loginInfo$ = this.userService.getCurrentLoginInfo().pipe(
      catchError((err) => {
        this.ErrorMessage = this.abcCore.error.getFullErrorMessage(err);
        return of(<LoginInfo>null);
      })
    );

    this.profiles$ = this.refreshToken$.pipe(
      tap((n) => {
        this.ErrorMessage = null;
      }),
      switchMap((n) =>
        this.profileService.getProfilesForCurrentUser().pipe(
          tap((data: any) => {
            if (localStorage.getItem('ProfileSettings')) {
              const profileSettings = JSON.parse(localStorage.getItem('ProfileSettings'))[0];
              const searchCondition = (element) => element.displayText === profileSettings.Value.displayText;
              this.selectedIndex = data.findIndex(searchCondition);
            }
          }),
          // delay(5000), // For testing/debugging loading phase
          catchError((err) => {
            this.ErrorMessage = this.abcCore.error.getFullErrorMessage(err);
            return of(<ProfileInfo[]>null);
          })
        )
      ),
      publishReplay(1),
      refCount()
    );

    this.isLoadingProfiles$ = merge(
      this.refreshToken$.pipe(mapTo(true)),
      this.profiles$.pipe(
        // Even errors indicate we're not still loading.
        catchError(() => of(undefined)),
        mapTo(false)
      )
    ).pipe(publishReplay(1), refCount());
  }

  ngOnDestroy(): void {
    this.ErrorMessage = null;
    this.refreshToken$.complete();
    this.destroy$.next(); // trigger the unsubscribe
    this.destroy$.complete(); // finalize & clean up the subject stream
  }

  logout(removeCurrentProfileFromStorage: boolean = false): void {
    this.ErrorMessage = null;

    if (removeCurrentProfileFromStorage) {
      this.profileService
        .removeCurrentProfile()
        .pipe(
          concatMap(() => this.userService.logoutCurrentUser(true)),
          takeUntil(this.destroy$)
        )
        .subscribe({ error: (err) => this.abcCore.error.publish(err) });
    } else {
      this.userService
        .logoutCurrentUser(true)
        .pipe(takeUntil(this.destroy$))
        .subscribe({ error: (err) => this.abcCore.error.publish(err) });
    }
  }

  cancel(): void {
    this.location.back();
  }

  refresh(): void {
    this.refreshToken$.next(undefined);
  }

  selectProfileAndApply(profileInfo: ProfileInfo): void {
    this.showLoader = true;
    if (profileInfo) {
      this.isApplyingProfile = true;
      this.selectedProfilesName = profileInfo.displayText;
      this.profileService
        .setCurrentProfile(profileInfo)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            // Navigate to this.ForwardToRoutePath
            let routePath: string;

            if (this.ForwardToRoutePath) {
              routePath = this.ForwardToRoutePath;
              // } else if (
              //   this.configService &&
              //   this.configService.configuration &&
              //   this.configService.configuration.DefaultRoute
              // ) {
              //   routePath = this.configService.configuration.DefaultRoute;
            } else {
              routePath = '/';
            }

            if (this.ForceReloadOnApply) {
              AuthenticationInterceptor.reload(routePath);
              this.isApplyingProfile = false;
            } else {
              this.router.navigate([routePath]).finally(() => (this.isApplyingProfile = false));
            }
          },
          error: (err) => {
            this.isApplyingProfile = false;
            this.ErrorMessage = this.abcCore.error.getFullErrorMessage(err);
          },
          complete: () => {},
        });
    }
  }

  getProgressTemplateContext(message: string): any {
    return { message: message };
  }
}
